import {
  Compatibility,
  EventDispatcher,
  EventStream,
  SceneObject,
  Vector2,
  viewportChangeListener,
} from '@cgs/wge';
import { BaseSlotView, ISlotPopupView } from '../../base_slot_view';

export const enum DrawOrderConstants {
  ProgressiveBonusDrawOrder = 600,
  MiniGameViewDrawOrder = 700,
  SlotLogoViewDrawOrder = 800,
  CharacterUnderContestPanelDrawOrder = 840,
  SlotHudViewDrawOrder = 850,
  ContestPanelDrawOrder = 851,
  IndicatorsDrawOrder = 970,
  TurboWinPanelDrawOrder = 852,
  Top = 100000,
  KingOfGame = 100010,
  BasePopupViewDrawOrder = 1100,
  TutorialDrawOrder = 12000,
  AutoSpinDrawOrder = 15000,
  BackGroundDrawOrder = -1200,
}

export const enum SlotPopups {
  StartFreeSpins = 'StartFreeSpins',
  EndFreeSpins = 'EndFreeSpins',
  Paytable = 'Paytable',
  BonusRecovery = 'BonusRecovery',
  EpicWin = 'EpicWin',
  Jackpot = 'Jackpot',
  Gift = 'Gift',
  MaxBetExceeded = 'MaxBetExceeded',
  SlotIntro = 'SlotIntro',
  SomethingWentWrong = 'SomethingWentWrong',
  NotEnoughBalance = 'NotEnoughBalance',
}

export class BaseSlotPopupView<T> extends BaseSlotView<T> implements ISlotPopupView {
  protected _soundNode: SceneObject | null;

  private readonly _popupId: string;
  private readonly _popup: SceneObject;
  protected readonly _closedDispatcher: EventDispatcher<void>;
  private readonly _closingDispatcher: EventDispatcher<void>;
  private readonly _shownDispatcher: EventDispatcher<void>;
  private readonly _showingDispatcher: EventDispatcher<void>;

  constructor(
    root: SceneObject,
    popup: SceneObject,
    soundNode: SceneObject | null,
    popupId: string
  ) {
    super(root);
    if (!root || !popup) {
      throw new Error('_root and _popupView cannot be null');
    }
    this._popupId = popupId;
    this._popup = popup;
    this._soundNode = soundNode;
    this._closedDispatcher = new EventDispatcher();
    this._closingDispatcher = new EventDispatcher();
    this._shownDispatcher = new EventDispatcher();
    this._showingDispatcher = new EventDispatcher();

    const resizeFunction = () => {
      if (Compatibility.isPortrait) {
        const sizeFrame = this._popup.findById('sizeFrame');
        if (!sizeFrame) {
          return;
        }
        const initialSizeX = sizeFrame.size.x;
        const initialPosX = this._popup.position.x;
        const initialPosY = this._popup.position.y;
        let windowWidth = window.innerWidth;

        const originalRatio = 1657 / 768;
        const windowRatio = window.innerHeight / window.innerWidth;

        if (windowRatio > originalRatio) {
          windowWidth = 768;
          const scale = windowWidth / initialSizeX;
          const offsetX = initialPosX * (1 - scale);
          const offsetY = initialPosY * (1 - scale);
          this._popup.position.addVector(new Vector2(offsetX, offsetY));
          this._popup.scale = new Vector2(scale, scale);
        } else {
          const newWindowWidth = windowWidth * (1657 / window.innerHeight);
          const scale = newWindowWidth / initialSizeX;
          const offsetX = initialPosX * (1 - scale);
          const offsetY = initialPosY * (1 - scale);
          this._popup.position.addVector(new Vector2(offsetX, offsetY));
          this._popup.scale = new Vector2(scale, scale);
        }
      } else {
        this._popup.position = new Vector2(0.0, 0.0);
        this._popup.scale = new Vector2(1.0, 1.0);
      }
    };

    viewportChangeListener.on(resizeFunction);
    resizeFunction();
  }

  public get popupId(): string {
    return this._popupId;
  }

  public get closed(): EventStream<void> {
    return this._closedDispatcher.eventStream;
  }

  public get closing(): EventStream<void> {
    return this._closingDispatcher.eventStream;
  }

  public get shown(): EventStream<void> {
    return this._shownDispatcher.eventStream;
  }

  public get showing(): EventStream<void> {
    return this._showingDispatcher.eventStream;
  }

  public get view(): SceneObject {
    return this._popup;
  }

  public show(): Promise<void> {
    if (!this._isShowing()) {
      const completer = new Promise<void>((resolve) => {
        const subscription = this.closed.listen(() => {
          resolve();
          subscription.cancel();
        });
      });

      this.onShowing();
      this._showingDispatcher.dispatchEvent();
      if (this._soundNode) {
        if (!this._soundNode.parent) {
          this._popup.addChild(this._soundNode);
        }
      }
      this.root.addChild(this._popup);

      this._popup.initialize();
      this._popup.z = this.drawOrder;
      this._popup.active = true;

      this.onShown();
      this._shownDispatcher.dispatchEvent();
      return completer;
    }
    return Promise.resolve();
  }

  public hide(): void {
    if (this._isShowing()) {
      this.onClosing();
      this._closingDispatcher.dispatchEvent();
      this._popup.z = 0;
      this._popup.active = false;
      this.root.removeChild(this._popup);
      this.onClosed();
      this._closedDispatcher.dispatchEvent();
      requestAnimationFrame(() => {
        this._popup.deinitialize();
      });
    }
  }

  protected get drawOrder(): number {
    return DrawOrderConstants.BasePopupViewDrawOrder;
  }

  public get isShown(): boolean {
    return this._isShowing();
  }

  private _isShowing(): boolean {
    return this.root.childs.includes(this._popup);
  }

  public postEvent(state: string): void {
    console.log('going to state: ' + state);
    this._popup.stateMachine!.switchToState(state);
  }

  public onShowing(): void {
    //
  }

  public onShown(): void {
    //
  }

  public onClosing(): void {
    //
  }

  public onClosed(): void {
    // /
  }
}
